import { css } from "lit";
/**
 * Shared styles for rendering markdown/rich-text HTML content (via unsafeHTML).
 *
 * Usage: import into any component's `static styles` and wrap rendered HTML
 * with `class="md-content"`.
 *
 * All values reference design tokens from color.ts, fonts.ts, styling.ts.
 */
export const markdownContentStyles = css `
  .md-content {
    font-family: var(--pd-default-font-content-family, sans-serif);
    color: inherit;
    font-size: var(--pd-default-font-content-size, 1rem);
    line-height: 1.6;
  }

  /* ── Block elements ─────────────────── */

  .md-content p {
    margin: 0 0 0.5em;
  }

  .md-content p:last-child {
    margin-bottom: 0;
  }

  .md-content ul,
  .md-content ol {
    margin: 0.5em 0;
    padding-left: 1.5em;
  }

  .md-content li {
    margin-bottom: 0.25em;
  }

  .md-content blockquote {
    border-left: 3px solid var(--pd-default-lighter-col, #ccc);
    margin: 0.5em 0;
    padding: var(--pd-spacing-xs, 0.25rem) var(--pd-spacing-sm, 0.5rem);
    color: var(--pd-default-font-muted-col, #666);
  }

  /* ── Headings ───────────────────────── */

  .md-content h1,
  .md-content h2,
  .md-content h3,
  .md-content h4 {
    font-family: var(--pd-default-font-title-family, sans-serif);
    color: inherit;
    margin: 0.75em 0 0.25em;
    line-height: 1.3;
  }

  .md-content h1 {
    font-size: 1.4em;
  }
  .md-content h2 {
    font-size: 1.2em;
  }
  .md-content h3 {
    font-size: 1.1em;
  }
  .md-content h4 {
    font-size: 1em;
  }

  /* ── Inline elements ────────────────── */

  .md-content a {
    color: var(--pd-default-font-link-col, var(--pd-default-col, inherit));
    text-decoration: underline;
  }

  .md-content strong {
    font-weight: 700;
  }

  .md-content em {
    font-style: italic;
  }

  /* ── Code ────────────────────────────── */

  .md-content code {
    background: var(--pd-default-bg-light-col, #f5f5f5);
    padding: 0.15em 0.4em;
    border-radius: var(--pd-radius-sm, 2px);
    font-size: 0.9em;
  }

  /* Wie inline-code und th: Tint-Hintergrund + geerbte Textfarbe — funktioniert
     in allen Themes. (--pd-default-bg-dark-col ist ein abgedunkelter
     Hintergrund-Tint, kein dunkles Surface; --pd-on-primary-col wäre Text auf
     Primärfarbe — die frühere Paarung war in Light- wie Dark-Theme unlesbar.) */
  .md-content pre {
    background: var(--pd-default-bg-dark-col, #e3f2f5);
    color: inherit;
    padding: var(--pd-spacing-sm, 0.5rem) var(--pd-spacing-md, 1rem);
    border-radius: var(--pd-radius-md, 4px);
    overflow-x: auto;
    font-size: 0.85em;
    margin: 0.5em 0;
  }

  .md-content pre code {
    background: none;
    padding: 0;
    color: inherit;
  }

  /* ── Table ──────────────────────────── */

  .md-content table {
    border-collapse: collapse;
    width: 100%;
    margin: 0.5em 0;
    font-size: 0.9em;
  }

  .md-content th,
  .md-content td {
    border: var(--pd-border-width, 1px) solid
      var(--pd-default-lighter-col, #ddd);
    padding: var(--pd-spacing-xs, 0.25rem) var(--pd-spacing-sm, 0.5rem);
    text-align: left;
  }

  .md-content th {
    background: var(--pd-default-bg-light-col, #f5f5f5);
    font-weight: 600;
  }

  /* ── First/last spacing cleanup ─────── */

  .md-content > :first-child {
    margin-top: 0;
  }

  .md-content > :last-child {
    margin-bottom: 0;
  }
`;
//# sourceMappingURL=markdown.js.map